/*
 * Copyright (c) 2016-2023 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */
/**
 * Given a 2d array grid structure provide a highlight/range selection of given cells
 */
export function gridRangeSelection() {
    return (target) => target.addInitializer((instance) => new GridRangeSelectionController(instance));
}
export class GridRangeSelectionController {
    constructor(host) {
        this.host = host;
        this.selectionActive = false;
        host.addController(this);
    }
    get enabled() {
        return this.host.rangeSelection !== false && !Array.from(this.host.rows).find(r => r.draggable);
    }
    async hostConnected() {
        await this.host.updateComplete;
        this.setupKeyboardListeners();
        this.setupMouseEvents();
    }
    setupMouseEvents() {
        const root = this.host.grid.shadowRoot ? this.host.grid.shadowRoot : this.host.grid;
        root.addEventListener('mousedown', (e) => {
            // preserve right click for context menus & keyboard mouse control https://apple.stackexchange.com/questions/32715/how-do-i-open-the-context-menu-from-a-mac-keyboard
            if (this.enabled && e.buttons === 1 && !e.ctrlKey) {
                this.setFirstCell(e);
            }
        });
        root.addEventListener('mouseover', (e) => {
            if (this.enabled) {
                this.setActiveCell(e.composedPath().find((i) => Array.from(this.host.cells).includes(i)));
            }
        });
        root.addEventListener('mouseup', () => {
            if (this.enabled) {
                this.stopSelection();
            }
        });
    }
    setupKeyboardListeners() {
        this.host.addEventListener('cdsKeyChange', (e) => {
            if (this.enabled && e.detail.code) {
                this.setActiveCell(e.detail.activeItem);
                if (!e.detail.shiftKey) {
                    this.stopSelection();
                    this.resetAllActiveCells();
                    this.host.dispatchEvent(new CustomEvent('rangeSelectionChange', { detail: [] }));
                }
            }
        });
        this.host.addEventListener('keydown', (e) => {
            if (this.enabled && e.code === 'ShiftLeft' && e.shiftKey && !this.selectionActive) {
                this.setFirstCell(e);
            }
        });
    }
    setFirstCell(e) {
        const firstCell = e.composedPath().find((i) => Array.from(this.host.cells).includes(i));
        if (firstCell) {
            this.firstCell = firstCell;
            this.selectionActive = true;
            this.resetAllActiveCells();
        }
    }
    setActiveCell(activeCell) {
        if (activeCell && this.selectionActive) {
            this.activeCell = activeCell;
            this.calculateSelection();
        }
    }
    stopSelection() {
        this.selectionActive = false;
    }
    resetAllActiveCells() {
        this.host.cells.forEach(cell => cell.removeAttribute('highlight'));
    }
    calculateSelection() {
        const x1 = parseInt(this.firstCell.ariaColIndex);
        const x2 = parseInt(this.activeCell.ariaColIndex);
        const y1 = parseInt(this.firstCell.parentElement?.ariaRowIndex);
        const y2 = parseInt(this.activeCell.parentElement?.ariaRowIndex);
        this.resetAllActiveCells();
        this.host.cells.forEach((cell) => {
            const colIndex = parseInt(cell.ariaColIndex);
            const rowIndex = parseInt(cell.parentElement?.ariaRowIndex);
            if ((x1 <= x2 && colIndex >= x1 && colIndex <= x2) || (x1 >= x2 && colIndex <= x1 && colIndex >= x2)) {
                if ((y1 <= y2 && rowIndex >= y1 && rowIndex <= y2) || (y1 >= y2 && rowIndex <= y1 && rowIndex >= y2)) {
                    cell.setAttribute('highlight', '');
                }
            }
        });
        this.host.dispatchEvent(new CustomEvent('rangeSelectionChange', {
            detail: Array.from(this.host.cells).filter(c => c.hasAttribute('highlight')),
        }));
    }
}
